import { Check } from "lucide-react";
import type React from "react";

import AppLogo from "@/components/app-logo";
import { FreeTrialBanner } from "@/components/free-trial-banner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type StepKey = "connect_domain" | "sign_up" | "branding" | "api_key";

type StepStatus = "pending" | "completed" | "skipped";

interface OnboardingStep {
  key: StepKey;
  status: StepStatus;
  completedAt?: string;
}

interface OnboardingState {
  currentStep: StepKey;
  steps: OnboardingStep[];
}

interface OnboardingLayoutProps {
  title: string;
  description?: string;
  onboarding: OnboardingState;
  onSkipStep?: () => void;
  onContinueStep?: () => void;
  children: React.ReactNode;
}

const STEP_LABELS: Record<StepKey, string> = {
  connect_domain: "Connect your domain",
  sign_up: "Sign up",
  branding: "Branding",
  api_key: "Generate API Key",
};

const STEP_KEYS: StepKey[] = ["connect_domain", "sign_up", "branding", "api_key"];

export function OnboardingLayout({
  title,
  description,
  onboarding,
  onSkipStep,
  onContinueStep,
  children,
}: OnboardingLayoutProps) {
  const fallbackSteps: OnboardingStep[] = STEP_KEYS.map((key) => ({
    key,
    status: "pending",
  }));

  const rawSteps = Array.isArray(onboarding.steps) && onboarding.steps.length > 0 ? onboarding.steps : fallbackSteps;

  const orderedSteps = [...rawSteps].sort((a, b) => stepOrder(a.key) - stepOrder(b.key));

  const totalSteps = orderedSteps.length || fallbackSteps.length;
  const currentIndex = Math.max(
    orderedSteps.findIndex((step) => step.key === onboarding.currentStep),
    0,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100/70 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <FreeTrialBanner />
      <div className="flex min-h-[calc(100vh-48px)] w-full flex-col bg-transparent lg:flex-row">
        <div className="flex flex-col bg-white px-6 py-8 lg:w-80 lg:px-8 dark:bg-gray-900">
          <div className="flex items-center gap-2">
            <AppLogo />
          </div>
          <p className="mt-6 text-xs font-semibold tracking-[0.3em] text-orange-500 uppercase dark:text-orange-200">
            Setup progress
          </p>
          <nav className="mt-6 flex flex-1 flex-col gap-4">
            {orderedSteps.map((step, index) => (
              <StepItem
                key={step.key}
                index={index}
                total={totalSteps}
                step={step}
                isActive={onboarding.currentStep === step.key}
              />
            ))}
          </nav>
        </div>

        <main className="flex-1 bg-white px-6 py-12 lg:px-16 dark:bg-gray-900">
          <div className="mx-auto w-full">
            <p className="text-xs font-semibold tracking-[0.35em] text-orange-400 uppercase dark:text-orange-200">
              Step {currentIndex + 1} of {totalSteps}
            </p>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl dark:text-white">
              {title}
            </h1>
            {description ? <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">{description}</p> : null}

            <div className="mt-10">{children}</div>

            {onSkipStep || onContinueStep ? (
              <div className="mt-12 border-t border-orange-100 pt-6 dark:border-orange-500/30">
                {onContinueStep ? (
                  <Button
                    type="button"
                    variant="default"
                    className="text-xs font-semibold tracking-[0.3em] text-orange-500 uppercase hover:text-orange-600 dark:bg-orange-500/20 dark:text-orange-600"
                    onClick={onContinueStep}
                  >
                    Continue
                  </Button>
                ) : null}
                {onSkipStep ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-xs font-semibold tracking-[0.3em] text-orange-500 uppercase hover:text-orange-600 dark:text-orange-200"
                    onClick={onSkipStep}
                  >
                    Skip this step
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
}

function StepItem({
  step,
  index,
  total,
  isActive,
}: {
  step: OnboardingStep;
  index: number;
  total: number;
  isActive: boolean;
}) {
  return (
    <div
      className={cn(
        "relative flex items-start gap-4 rounded-2xl border border-transparent bg-orange-50/80 px-5 py-4 text-orange-900 transition-colors dark:bg-orange-500/15 dark:text-orange-100",
        isActive &&
          "border-orange-300 bg-white text-orange-900 shadow-sm dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-100",
        step.status === "completed" &&
          !isActive &&
          "bg-orange-100/70 text-orange-800 dark:bg-orange-500/25 dark:text-orange-100",
      )}
    >
      <StepIcon status={step.status} displayIndex={index + 1} isActive={isActive} />
      <div className="flex-1">
        <p className="text-sm leading-tight font-semibold text-neutral-800 dark:text-neutral-100">
          {STEP_LABELS[step.key]}
        </p>
        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-300">
          {step.status === "skipped" ? "Marked as skipped" : `Step ${index + 1} of ${total}`}
        </p>
      </div>
    </div>
  );
}

function StepIcon({ status, displayIndex, isActive }: { status: StepStatus; displayIndex: number; isActive: boolean }) {
  if (status === "completed") {
    return (
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500 text-white shadow">
        <Check className="h-5 w-5" />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold",
        status === "skipped"
          ? "border-orange-200 text-orange-300 dark:border-orange-500/40 dark:text-orange-300"
          : "border-orange-300 text-orange-600 dark:border-orange-500/50 dark:text-orange-200",
        isActive && "bg-white text-orange-500 dark:bg-orange-500/30",
      )}
    >
      {displayIndex}
    </span>
  );
}

function stepOrder(step: StepKey) {
  switch (step) {
    case "connect_domain":
      return 1;
    case "sign_up":
      return 2;
    case "branding":
      return 3;
    case "api_key":
      return 4;
    default:
      return 99;
  }
}
